/* eslint-disable @next/next/no-img-element */
import { formatCurrency } from '@/helpers/format';
import usePrefix from '@/hooks/usePrefix';
import dayjs from 'dayjs';
import { useMobxStores } from '@/stores';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, MutableRefObject } from 'react';
import ButtonStatus from './ButtonStatus2';
import SearchImage from './SearchImage';
import { isShowAnyHighlights } from '@/helpers/highlights';
import { CDN1, CDN2 } from '@/helpers/api';
import { mapRateSearchName, getFacilityDisplayName } from '@/utils/tools';
import { getParkingAtPath, getUrlCodeOrSlug } from '@/utils/newparkingatroutes';
import NeedMoreData from './NeedMoreData';
import { IAirport } from '@/types';
import { isServer } from '@/utils/isServer';
import { setStorageCheckinCheckout } from '@/hooks/useLocalstorage';
import { useTranslation } from 'react-i18next';
import NewCardHighlightB from './FacilityHighlights/NewCardHighlightB';
import Link from 'next/link';
import CancellationInformationB from './FacilityHighlights/CancellationInformationB';
import { StarFilled } from '@ant-design/icons';

export function checkShowFull(facility_url_code: string) {
  return !facility_url_code?.toLocaleLowerCase().startsWith('arv');
}

export const CardPriceContent = ({
  configInfo,
  item,
  isNewLanding,
  isParkingAt,
  checkin,
  checkout,
  onBookClick,
}) => {
  const prefix = usePrefix();
  const { searchStore } = useMobxStores();

  const hideParkingAtPrice =
    item.facility_url_code?.startsWith('rl') &&
    item.date_sold_out > 0 &&
    Number(item.facility_selling_price ?? 0) <= 0;

  const dcheckin = dayjs(checkin);
  const dcheckout = dayjs(checkout);
  const dateLabel =
    isParkingAt && dcheckin.isValid() && dcheckout.isValid()
      ? `${dcheckin.format('MMM D')} – ${dcheckout.format('MMM D')} (${Math.max(dcheckout.diff(dcheckin, 'day'), 1)} days)`
      : '';

  const handleBookClick = onBookClick
    ? (e) => {
      e.preventDefault();
      onBookClick(item);
    }
    : undefined;

  if (!isNewLanding && item.date_sold_out > 0) {

    return (
      <ButtonStatus
        status={searchStore.facilityStatus(item)}
        facility_url_code={item.facility_url_code}
        minDays={item.facility_min_days}
        maxDays={item.facility_max_days}
        configInfo={configInfo}
        item={item}
        isNewCardList
      />
    );
  }

  return (
    <div className="w-full">
      <div className={`flex ${isParkingAt ? "flex-row items-center md:flex-col md:items-start" : "flex-row items-start"} justify-between gap-3 lg:items-center`}>
        <CancellationInformationB highlights2={item.highlights2} highlights={item.highlights} facility={item} extraClass="md:hidden" />
        {!isParkingAt && (
          <CancellationInformationB highlights2={item.highlights2} highlights={item.highlights} facility={item} extraClass="hidden md:flex" />
        )}

        <div className={`flex items-center gap-3  ${!isParkingAt ? " md:gap-6 lg:gap-8 2xl:gap-10 flex-row" : "flex-col"}`}>
          <div className="text-right leading-[1.1]">
            {dateLabel && (
              <div className="whitespace-nowrap text-[10.5px] mb-0.5 font-semibold text-[#687788]">
                {dateLabel}
              </div>
            )}
            {!hideParkingAtPrice && (
              <span className="text-[24px] font-extrabold text-[#1E2A38]">
                {item.facilityParkingType == 3 || item.facilityParkingType == 4 ? (
                  <>
                    {formatCurrency({
                      value: item.facility_solid_selling_price
                        ? item.facility_solid_selling_price
                        : item.facility_afee,
                      locale: 'en-US',
                      currency: 'USD',
                    })}{' '}
                    <small className="text-[11px] text-[#687788]">
                      {item.facilityParkingType == 3
                        ? 'Sleep and Park'
                        : 'Monthly Park'}
                    </small>
                  </>
                ) : (
                  <>
                    {isNewLanding && (
                      <small className="text-[11px] mr-1">
                        Starting at{' '}
                      </small>
                    )}
                    {!!item.currency_facility_selling_price
                      ? formatCurrency({
                        value: item.currency_facility_selling_price,
                        locale: 'en-CA',
                        currency: item.currency_name,
                      })
                      : formatCurrency({
                        value: item.facility_selling_price,
                        locale: 'en-US',
                        currency: 'USD',
                      })}{' '}
                    {!isNewLanding && (
                      <small className="text-[11px]] text-[#687788]">
                        {mapRateSearchName(item)}
                      </small>
                    )}
                  </>
                )}
              </span>
            )}
            {!isNewLanding && (
              <div className="mt-0.5 md:mt-1 text-[10.5px] text-[#687788]">
                {item.facilityParkingType == 3 ||
                  item.facilityParkingType == 4 ? (
                  <>
                    {formatCurrency({
                      value: item.facility_total_price,
                      locale: 'en-US',
                      currency: 'USD',
                    })}
                  </>) : (<>
                    {!!item.currency_total_price ?
                      formatCurrency({
                        value: item.currency_total_price,
                        locale: 'en-CA',
                        currency: item.currency_name,
                      })
                      : formatCurrency({
                        value: item.facility_total_price,
                        locale: 'en-US',
                        currency: 'USD',
                      })}
                  </>)}{' '}
                <small className="text-[10.5px] text-[#687788]">
                  total · taxes incl.
                </small>
              </div>
            )}
          </div>
          <Link href={!isNewLanding && item.date_sold_out > 0 ? '#' : `${prefix}/${getParkingAtPath(item)}/${getUrlCodeOrSlug(item)}`} passHref legacyBehavior>
            <span onClick={handleBookClick} className="hidden whitespace-nowrap rounded-full bg-[#E57715] px-5 py-2.5 text-[13px] font-bold text-white shadow-[0_2px_4px_rgba(229,119,21,.35)] transition-colors hover:bg-[#D96A11] md:inline-flex md:items-center md:justify-center cursor-pointer">
              Book My Spot
            </span>
          </Link>
        </div>
      </div>
      <Link href={!isNewLanding && item.date_sold_out > 0 ? '#' : `${prefix}/${getParkingAtPath(item)}/${getUrlCodeOrSlug(item)}`} passHref legacyBehavior>
        <span onClick={handleBookClick} className={`mt-2.5 inline-flex w-full items-center justify-center rounded-full bg-[#E57715] px-4 py-2.5 text-sm font-bold text-white shadow-[0_2px_4px_rgba(229,119,21,.35)] transition-colors hover:bg-[#D96A11] md:hidden cursor-pointer`}>
          Book My Spot
        </span>
      </Link>
    </div>
  );
};

type CardProps = {
  configInfo: any;
  item: any;
  index: any;
  cardsRef?: MutableRefObject<any>;
  setHoverIndex?: (index: number) => void;
  isNewLanding?: boolean;
  clearHoverIndex?: () => void;
  checkin?: string;
  checkout?: string;
  imagesCount?: number;
  imagesRef?: MutableRefObject<HTMLElement | null>;
  isParkingAt?: boolean;
  onBookClick?: (item: any) => void;
  hideMobilePriceContent?: boolean;
  isCheckoutSummary?: boolean;
  isStatic?: boolean;
  isReferExternal?: boolean;
  airportInitials?: string;
  airportDisplayType?: number;
};

export const Card = ({
  configInfo,
  item,
  index,
  cardsRef,
  setHoverIndex,
  isNewLanding,
  clearHoverIndex,
  checkin,
  checkout,
  imagesCount = undefined,
  imagesRef = undefined,
  isParkingAt,
  onBookClick = undefined,
  hideMobilePriceContent = false,
  isCheckoutSummary = false,
  isStatic = false,
  isReferExternal = false,
  airportInitials,
  airportDisplayType,
}: CardProps) => {
  const prefix = usePrefix();
  const highlights = item.highlights ?? [];
  const highlights2 = item.highlights2 ?? [];

  const UnbrandedImages = [
    CDN2() + '/staticmyapp/cards/logo-p.svg',
  ];

  let lastUnbrandedIndex = null;

  const getUnbrandedImage = () => {
    let randomIndex = index % UnbrandedImages.length;
    if (randomIndex === lastUnbrandedIndex) {
      randomIndex = (randomIndex + 1) % UnbrandedImages.length;
    }
    lastUnbrandedIndex = randomIndex;
    return UnbrandedImages[randomIndex];
  };

  const imageFacilityId = Math.abs(Number(item.facility_id));

  let imageUrl = item.facility_is_image_uploaded
    ? `${CDN1}/upload/${configInfo.environment}/${imageFacilityId}`
    : getUnbrandedImage();

  const arriveLogo = highlights.find((x) => x.type === 'arrive_logo_url');
  if (arriveLogo) {
    imageUrl = arriveLogo.description;
  }

  const photosCount = item.images_parking_at?.length ?? imagesCount ?? 0;

  const handlePhotosClick = (e) => {
    if (!imagesRef?.current) return;
    e.preventDefault();
    e.stopPropagation();
    imagesRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const priceContent = (
    <CardPriceContent
      configInfo={configInfo}
      item={item}
      isNewLanding={isNewLanding}
      isParkingAt={isParkingAt}
      checkin={checkin}
      checkout={checkout}
      onBookClick={onBookClick}
    />
  );
  const facilityDisplayName = getFacilityDisplayName(item);

  const cardContent = (
    <article
      key={item.facility_id}
      ref={(el) => (cardsRef.current[index] = el)}
      className={`new-card h-full ${isCheckoutSummary ? "w-full" : ""} ${!isParkingAt && "border"} lg:border-none
    font-['Helvetica_Neue',_Arial,_sans-serif]
    ${isNewLanding ? "bg-white content-center" : ""}
    ${!isNewLanding && item.date_sold_out > 0
          ? "pointer-events-none"
          : `${isStatic ? "" : "hover:-translate-y-0.5 cursor-pointer"} border-[#E5E7EB]`
        }
    ${!isNewLanding && item.date_sold_out > 0
          ? `relative mx-auto bg-white rounded-[0.675rem] ${!isParkingAt && "shadow-lg"}  overflow-hidden ${item.selected ? "shadow-2xl" : ""
          } opacity-60`
          : `relative rounded-[14px] overflow-hidden ${!isParkingAt && "shadow"} ${item.selected
            ? `${!isParkingAt && "shadow-lg"} -translate-y-0.5`
            : `${!isParkingAt && "shadow-md hover:shadow-lg"} `
          }`
        }`}
      onMouseEnter={() => setHoverIndex && setHoverIndex(index)}
      onMouseLeave={() => clearHoverIndex && clearHoverIndex()}
    >
      <div className={`grid items-stretch gap-x-3 px-3.5 pt-3 text-left md:grid-cols-[118px_minmax(0,1fr)] md:gap-x-4 md:px-4 md:py-3.5 ${isCheckoutSummary
        ? 'grid-cols-1 gap-y-3 md:gap-y-0'
        : 'grid-cols-[88px_minmax(0,1fr)] sm:grid-cols-[98px_minmax(0,1fr)]'
        }`}>
        <div className={`flex shrink-0 flex-col items-center justify-center gap-[7px] ${isCheckoutSummary
          ? 'border-b border-[#E5E7EB] pb-3 md:row-span-2 md:border-b-0 md:border-r md:pb-0 md:pr-4'
          : 'row-span-2 border-r border-[#E5E7EB] pr-3 md:pr-4'
          }`}>
          <div
            className={`relative w-[72px] h-[72px] sm:w-[78px] sm:h-[78px] md:w-[96px] md:h-[96px] overflow-hidden rounded-xl`}
          >
            <SearchImage
              src={imageUrl}
              native={!!arriveLogo}
              blurDataURL={getUnbrandedImage()}
              errorImage={getUnbrandedImage()}
              quality={100}
              placeholder="blur"
              className="absolute inset-0 w-full h-full object-contain rounded-xl"
              fill
              alt={`Airport: ${facilityDisplayName} Background`}
              key={item.facility_id}
            />
            <p className="absolute bottom-1 left-1 text-transparent opacity-50 !text-xs cursor-default">
              {item.facility_url_code}
            </p>
          </div>

          {Number.isFinite(Number(item.facility_review_avg)) &&
            Number(item.facility_review_avg) > 0 && (
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#F5F7FA] px-2.5 py-[3px] text-[11.5px] text-[#526171]">
                <StarFilled className="w-3 h-3 text-[#FAE251]" />
                <b className="text-[#1E2A38]">{Number(item.facility_review_avg).toFixed(1)}</b>
                <span>({item.num_review ?? 0})</span>
              </span>
            )}
        </div>

        <div className={`flex ${isParkingAt ? "flex-col md:flex-row" : "flex-col"} justify-between`}>

          <div className="min-w-0 flex flex-col">
            {isParkingAt && !isCheckoutSummary ? (
              ""
            ) :
              <div className="min-w-0">
                <h3 className="font-black text-[#2b2b2b] text-[15.5px] leading-[1.2] break-words">{facilityDisplayName}</h3>
              </div>
            }

            {isShowAnyHighlights(highlights, highlights2) ?
              <>
                <NewCardHighlightB highlights2={highlights2} highlights={highlights} facility={item} photosCount={photosCount} onPhotosClick={handlePhotosClick} isParkingAt={isParkingAt} hideShuttle={isCheckoutSummary} isCheckoutSummary={isCheckoutSummary} airportInitials={airportInitials} airportDisplayType={airportDisplayType} />
                {isParkingAt && (
                  <CancellationInformationB
                    highlights2={highlights2}
                    highlights={highlights}
                    facility={item}
                    extraClass={isCheckoutSummary ? 'flex' : 'hidden md:flex'}
                  />
                )}
              </>
              : (
                <></>
              )}

          </div>

          {!isCheckoutSummary && (!isReferExternal || item.date_sold_out > 0) && (
            <div className={`hidden md:block mt-2.5 border-t border-[#E5E7EB] pt-2.5 ${isParkingAt ? "md:mt-0 md:border-t-0 md:pt-0" : ""}`}>
              {priceContent}
            </div>
          )}
        </div>
      </div>
      {!isCheckoutSummary && (!isReferExternal || item.date_sold_out > 0) && !hideMobilePriceContent && (
        <div className="px-3.5 pb-3 mt-2.5 border-t border-[#E5E7EB] pt-2.5 md:hidden">
          {priceContent}
        </div>
      )}
    </article>
  );

  if (isStatic) {
    return cardContent;
  }

  return (
    <Link
      href={
        !isNewLanding && item.date_sold_out > 0
          ? "#"
          : `${prefix}/${getParkingAtPath(item)}/${getUrlCodeOrSlug(item)}`
      }
      key={item.facility_id + index}
      passHref
      legacyBehavior
    >
      {cardContent}
    </Link>
  );
};

function removeFakeSoldoutCard(cards) {
  for (let i = 0; i < cards.length; i++) {
    for (let j = i + 1; j < cards.length; j++) {
      if (cards[i].facility_url_code === cards[j].facility_url_code) {
        cards.splice(j, 1)
      }
    }
  }
  return cards;
}
function removeExpensiveSoldoutCard(cards) {
  for (let i = 0; i < cards.length; i++) {
    for (let j = i + 1; j < cards.length; j++) {
      if (cards[i].facility_url_code === cards[j].facility_url_code) {
        cards[i] = cards[j];
        cards.splice(j, 1)
      }
    }
  }
  return cards;
}

function SearchList({
  configInfo,
  checkin,
  checkout,
  deviceType,
  searchAlternate,
  currentAirport,
  isNewLanding,
  isCity,
  testScope,
  setHoverIndex,
  clearHoverIndex,
  imagesCount,
  imagesRef,
  isParkingAt,
}: {
  configInfo: any;
  checkin: string;
  checkout: string;
  deviceType: string;
  searchAlternate: any;
  currentAirport?: IAirport;
  isNewLanding?: boolean;
  isCity?: boolean;
  testScope?: any;
  setHoverIndex?: (index: number) => void;
  clearHoverIndex?: () => void;
  imagesCount?: number;
  imagesRef?: MutableRefObject<HTMLElement | null>;
  isParkingAt?: boolean;
}) {
  const { searchStore } = useMobxStores();
  const cardsRef = useRef<any>([]);
  const { t } = useTranslation();

  const hasHighlight = searchStore.filteredFacilities?.some(
    (x) => x.facility_branded == 1
  );

  let baseFacilities =
    searchStore.filteredFacilities ?? searchAlternate?.result;

  if (isNewLanding) {
    baseFacilities = removeExpensiveSoldoutCard([...baseFacilities])
  }
  else {
    baseFacilities = removeFakeSoldoutCard([...baseFacilities])
  }


  const showLowestPriceMessage = baseFacilities?.some(
    (item) => item.facility_disable_lowest_price === 0
  );
  useEffect(() => {
    if (!isServer) {
      setStorageCheckinCheckout(checkin, checkout);
    }
  }, [checkin, checkout]);
  return (
    // <Skeleton
    //   active
    //   loading={searchStore.isLoading}
    //   className="max-w-2xl mx-auto"
    // >
    <>
      {baseFacilities?.map((item, index) => {
        return (
          <div key={index + item.facility_url_code} className="mb-4 px-2">
            <Card
              configInfo={configInfo}
              item={item}
              index={index}
              cardsRef={cardsRef}
              setHoverIndex={setHoverIndex}
              clearHoverIndex={clearHoverIndex}
              isNewLanding={isNewLanding}
              checkin={checkin}
              checkout={checkout}
              imagesCount={imagesCount}
              imagesRef={imagesRef}
              isParkingAt={isParkingAt}
              onBookClick={undefined}
              airportDisplayType={currentAirport?.airport_display_type}
            />
          </div>
        );
      })}

      {searchStore.filteredFacilities?.length === 0 && !isParkingAt && (
        <NeedMoreData airport={currentAirport} />
      )}
    </>
  );
}

export default observer(SearchList);
